import { z } from 'zod';

import { EDIT_PROFILE_COPY, PROFILE_POLICY } from './constants';

/**
 * `GET /profile/tracking`, read whole — `meta` sits beside `data` (greenway_api
 * `profile.controller.ts:83-129`). `identity` feeds the panel header: the session user from a fresh
 * login carries no email or phone (the login response's user is `{ id, firstName, lastName, role }`),
 * so the header prefers this and falls back to the session user only while it loads.
 * `null` on a body value means "unavailable": the panel renders "-".
 *
 * A malformed counter block degrades to unavailable rather than failing the parse, so one bad
 * value cannot blank the panel. `partial` is derived from what was read, not copied from
 * `meta.partial`, so the "-" and the toast can never disagree.
 */
const deviceCountsSchema = z.object({ total: z.number(), off: z.number(), running: z.number() });
const supportSchema = z.object({ phone: z.string(), email: z.string() });

export const profileEnvelopeSchema = z
  .object({
    data: z.object({
      firstName: z.string(),
      lastName: z.string(),
      email: z.string().nullish(),
      phone: z.string().nullish(),
      deviceCounts: z.unknown(),
      supportTickets: z.object({ activeCount: z.number() }).nullish().catch(null),
      support: z.unknown(),
    }),
    meta: z
      .object({ unavailableFields: z.array(z.string()).catch([]) })
      .partial()
      .nullish(),
  })
  .transform(({ data, meta }) => {
    const unavailable = new Set(meta?.unavailableFields ?? []);
    const read = <T>(field: 'deviceCounts' | 'support', schema: z.ZodType<T>) => {
      if (unavailable.has(field)) return null;
      const parsed = schema.safeParse(data[field]);
      return parsed.success ? parsed.data : null;
    };
    const deviceCounts = read('deviceCounts', deviceCountsSchema);
    const support = read('support', supportSchema);
    return {
      identity: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email ?? null,
        phone: data.phone ?? null,
      },
      deviceCounts,
      activeTickets: data.supportTickets?.activeCount ?? 0,
      support,
      partial: deviceCounts === null || support === null,
    };
  });

export type ProfilePanel = z.infer<typeof profileEnvelopeSchema>;

/** What a code send/resend returns (`context.md` § Backend contract). */
export const codeDispatchSchema = z.object({
  referenceId: z.string(),
  maskedPhone: z.string(),
  otpExpiresAt: z.string(),
  resendCooldownSeconds: z.number(),
  resendAttemptsRemaining: z.number(),
});
export type CodeDispatch = z.infer<typeof codeDispatchSchema>;

const nameSchema = z
  .string()
  .trim()
  .min(1, EDIT_PROFILE_COPY.INVALID_NAME)
  .max(PROFILE_POLICY.NAME_MAX, EDIT_PROFILE_COPY.INVALID_NAME);

/** Edit Profile (US-117543 §B, grilling Q15). A blank email is allowed; the caller sends it as `null`. */
export const editProfileSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  email: z
    .string()
    .trim()
    .max(PROFILE_POLICY.EMAIL_MAX, EDIT_PROFILE_COPY.INVALID_EMAIL)
    .refine((v) => v === '' || z.email().safeParse(v).success, EDIT_PROFILE_COPY.INVALID_EMAIL),
});

/** `PUT /profile/tracking` 200 `data` (greenway_api profile controller). */
export const updatedProfileSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
});

/** Edit Profile's save (US-117543 §C). A blank email goes as `null`, never `''`. */
export type UpdateProfileInput = Pick<
  z.infer<typeof updatedProfileSchema>,
  'firstName' | 'lastName' | 'email'
>;
